import { Controller } from "@hotwired/stimulus"

const CATEGORY_ICONS = {
  "Orders": `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"/></svg>`,
  "Users": `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>`,
  "Products": `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>`,
  "RMAs": `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z"/></svg>`,
  "Contact Forms": `<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>`
}

const CATEGORY_COLORS = {
  "Orders": "text-blue-500 bg-blue-50",
  "Users": "text-purple-500 bg-purple-50",
  "Products": "text-emerald-500 bg-emerald-50",
  "RMAs": "text-amber-500 bg-amber-50",
  "Contact Forms": "text-rose-500 bg-rose-50"
}

export default class extends Controller {
  static targets = ["input", "dropdown"]

  connect() {
    this.timeout = null
    this.selectedIndex = -1
    this.resultItems = []
    this._onClickOutside = this.clickOutside.bind(this)
    this._onSlashKey = this.slashKey.bind(this)
    document.addEventListener("click", this._onClickOutside)
    document.addEventListener("keydown", this._onSlashKey)
  }

  disconnect() {
    document.removeEventListener("click", this._onClickOutside)
    document.removeEventListener("keydown", this._onSlashKey)
    if (this.timeout) clearTimeout(this.timeout)
  }

  slashKey(event) {
    if (event.key === "/" && !this.isInputActive()) {
      event.preventDefault()
      this.inputTarget.focus()
    }
  }

  isInputActive() {
    const tag = document.activeElement?.tagName
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || document.activeElement?.isContentEditable
  }

  onFocus() {
    const query = this.inputTarget.value.trim()
    if (query.length >= 2 && this.dropdownTarget.innerHTML) {
      this.dropdownTarget.classList.remove("hidden")
    }
  }

  onBlur() {
    // Delay to allow click on results
    setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        // Don't close — clickOutside handles it
      }
    }, 150)
  }

  onInput() {
    if (this.timeout) clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.search(), 300)
  }

  onKeydown(event) {
    if (!this.dropdownTarget.classList.contains("hidden")) {
      switch (event.key) {
        case "ArrowDown":
          event.preventDefault()
          this.moveSelection(1)
          break
        case "ArrowUp":
          event.preventDefault()
          this.moveSelection(-1)
          break
        case "Enter":
          event.preventDefault()
          this.selectCurrent()
          break
        case "Escape":
          this.close()
          this.inputTarget.blur()
          break
      }
    } else if (event.key === "Escape") {
      this.inputTarget.blur()
    }
  }

  async search() {
    const query = this.inputTarget.value.trim()
    if (query.length < 2) {
      this.close()
      return
    }

    this.showLoading()

    try {
      const response = await fetch(`/admin/search.json?q=${encodeURIComponent(query)}`)
      const data = await response.json()
      this.renderResults(data.results, query)
    } catch {
      this.close()
    }
  }

  showLoading() {
    this.dropdownTarget.innerHTML = `
      <div class="flex items-center gap-2 px-4 py-4 text-sm text-gray-400">
        <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        Searching...
      </div>
    `
    this.dropdownTarget.classList.remove("hidden")
  }

  renderResults(results, query) {
    this.selectedIndex = -1
    this.resultItems = []

    if (!results || results.length === 0) {
      this.dropdownTarget.innerHTML = `
        <div class="px-4 py-8 text-center">
          <div class="text-gray-300 mb-2">
            <svg class="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <div class="text-sm text-gray-500">No results for "<span class="font-medium text-gray-700">${this.escapeHtml(query)}</span>"</div>
        </div>
      `
      this.dropdownTarget.classList.remove("hidden")
      return
    }

    let html = ""
    let itemIndex = 0
    let totalItems = 0
    results.forEach(g => totalItems += g.items.length)

    results.forEach((group, groupIdx) => {
      const icon = CATEGORY_ICONS[group.category] || ""
      const colorClass = CATEGORY_COLORS[group.category] || "text-gray-500 bg-gray-50"

      html += `<div class="px-3 py-2 flex items-center gap-2 ${groupIdx > 0 ? "border-t border-gray-100" : ""}">
        <span class="flex items-center justify-center w-6 h-6 rounded-md ${colorClass}">${icon}</span>
        <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">${group.category}</span>
        <span class="text-[10px] text-gray-300 bg-gray-100 rounded-full px-1.5 py-0.5 font-medium">${group.items.length}</span>
      </div>`

      group.items.forEach((item) => {
        html += `
          <a href="${item.url}" data-index="${itemIndex}"
             class="search-result-item group flex items-center gap-3 px-3 py-2 mx-1.5 mb-0.5 rounded-lg cursor-pointer transition-colors duration-100"
             data-action="mouseenter->admin-search#onMouseEnter">
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-800 truncate group-hover:text-blue-600 transition-colors">${this.highlightMatch(item.title || "", query)}</div>
              <div class="text-xs text-gray-400 truncate">${this.highlightMatch(item.subtitle || "", query)}</div>
            </div>
            <svg class="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </a>
        `
        itemIndex++
      })
    })

    // Footer with keyboard hints
    html += `
      <div class="px-3 py-2 border-t border-gray-100 bg-gray-50/80 flex items-center gap-3 text-[10px] text-gray-400 rounded-b-xl">
        <span class="flex items-center gap-1"><kbd class="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono shadow-sm">&uarr;</kbd><kbd class="px-1 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono shadow-sm">&darr;</kbd> navigate</span>
        <span class="flex items-center gap-1"><kbd class="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono shadow-sm">&crarr;</kbd> open</span>
        <span class="flex items-center gap-1"><kbd class="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-[10px] font-mono shadow-sm">esc</kbd> close</span>
        <span class="ml-auto text-gray-300">${totalItems} result${totalItems !== 1 ? "s" : ""}</span>
      </div>
    `

    this.dropdownTarget.innerHTML = html
    this.dropdownTarget.classList.remove("hidden")
    this.resultItems = this.dropdownTarget.querySelectorAll(".search-result-item")
  }

  highlightMatch(text, query) {
    const escaped = this.escapeHtml(text)
    if (!query) return escaped
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi")
    return escaped.replace(regex, `<mark class="bg-yellow-100 text-yellow-800 rounded px-0.5">$1</mark>`)
  }

  moveSelection(delta) {
    if (this.resultItems.length === 0) return

    if (this.selectedIndex >= 0 && this.selectedIndex < this.resultItems.length) {
      this.resultItems[this.selectedIndex].classList.remove("bg-blue-50")
    }

    this.selectedIndex += delta
    if (this.selectedIndex < 0) this.selectedIndex = this.resultItems.length - 1
    if (this.selectedIndex >= this.resultItems.length) this.selectedIndex = 0

    this.resultItems[this.selectedIndex].classList.add("bg-blue-50")
    this.resultItems[this.selectedIndex].scrollIntoView({ block: "nearest" })
  }

  selectCurrent() {
    if (this.selectedIndex >= 0 && this.selectedIndex < this.resultItems.length) {
      window.location.href = this.resultItems[this.selectedIndex].getAttribute("href")
    }
  }

  onMouseEnter(event) {
    if (this.selectedIndex >= 0 && this.selectedIndex < this.resultItems.length) {
      this.resultItems[this.selectedIndex].classList.remove("bg-blue-50")
    }
    this.selectedIndex = parseInt(event.currentTarget.dataset.index)
    event.currentTarget.classList.add("bg-blue-50")
  }

  close() {
    this.dropdownTarget.classList.add("hidden")
    this.selectedIndex = -1
    this.resultItems = []
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
